import { type AgentsExitContext } from '@lib/analytics/agentsExit';
import { exchangeShortLivedToken } from '@lib/network/tokenExchange';
import { showToast } from '@lib/utils/toastmaster';
import { trackingStore } from '@stores/Tracking';
import { userStore } from '@stores/User';
import type React from 'react';

/**
 * Chats API — talks to the StarZero Agents (vibedit) chats service.
 * Mirrors the same two-env-var split as podcasts.ts:
 *
 *  - `CHAT_API_URL` — the API host (e.g. `https://api.vibedit.ai`).
 *    All `/chats/*` HTTP calls go here. Same backend the chat web app
 *    talks to from its own `ChatListStore`.
 *  - `CHAT_URL` — the StarZero Agents web app origin
 *    (e.g. `https://chat.starzero.ai`). Used purely for deep-link URLs
 *    (`/chat/new`, `/chat/{id}`) — never for HTTP calls.
 *
 * Field shapes mirror the vibedit `UserChatDocument` — only the fields
 * the navbar consumes are typed. Backend may add or omit fields
 * depending on lifecycle; consumers should optional-chain everything
 * except `_id`.
 */

const CHAT_URL = process.env.CHAT_URL?.replace(/\/$/, '') ?? '';
const CHAT_API_URL = process.env.CHAT_API_URL?.replace(/\/$/, '') ?? '';

export interface ChatListItem {
  _id: string;
  title?: string;
  starred?: boolean;
  createdAt?: number;
  updatedAt?: number;
  score?: number;
}

interface FetchChatsOptions {
  query?: string;
  signal?: AbortSignal;
}

function bearerHeaders(): HeadersInit | null {
  const token = userStore.token;
  if (!token) return null;
  return { Authorization: `Bearer ${token}` };
}

function jsonHeaders(): HeadersInit | null {
  const auth = bearerHeaders();
  if (!auth) return null;
  return { ...auth, 'Content-Type': 'application/json' };
}

/**
 * GET ${CHAT_API_URL}/chats?query={query}
 *
 * Returns the user's chats sorted by starred-first then most-recent.
 * Returns an empty array when `CHAT_API_URL` isn't set or the user
 * isn't logged in — matches the silent-fail pattern in podcasts.ts.
 */
export async function listChats(options: FetchChatsOptions = {}): Promise<ChatListItem[]> {
  if (!CHAT_API_URL) {
    console.warn('CHAT_API_URL not set — skipping chats fetch.');
    return [];
  }
  const headers = bearerHeaders();
  if (!headers) return [];

  const params = new URLSearchParams({ query: options.query ?? '' });
  const res = await fetch(`${CHAT_API_URL}/chats?${params.toString()}`, {
    headers,
    signal: options.signal,
  });
  if (!res.ok) {
    throw new Error(`Chats API ${res.status}`);
  }
  const chats = (await res.json()) as ChatListItem[];

  // Sort: by score if the response is a search result, else starred-first
  // then newest-first by createdAt. Matches vibedit's ChatListStore.
  if ((chats?.[0]?.score ?? 0) > 0) {
    chats.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  } else {
    chats.sort((a, b) => {
      if ((a.starred ?? false) === (b.starred ?? false)) {
        return (b.createdAt ?? 0) - (a.createdAt ?? 0);
      }
      return a.starred ? -1 : 1;
    });
  }
  return chats;
}

/** DELETE ${CHAT_API_URL}/chats/{id} */
export async function deleteChat(id: string): Promise<void> {
  const headers = bearerHeaders();
  if (!CHAT_API_URL || !headers) return;
  const res = await fetch(`${CHAT_API_URL}/chats/${id}`, { method: 'DELETE', headers });
  if (!res.ok) throw new Error(`Chats API ${res.status}`);
}

/** POST ${CHAT_API_URL}/chats/{id}/star — body `{ starred }` */
export async function toggleStarChat(id: string, starred: boolean): Promise<void> {
  const headers = jsonHeaders();
  if (!CHAT_API_URL || !headers) return;
  const res = await fetch(`${CHAT_API_URL}/chats/${id}/star`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ starred }),
  });
  if (!res.ok) throw new Error(`Chats API ${res.status}`);
}

/** POST ${CHAT_API_URL}/chats/{id}/name — body `{ name }` */
export async function renameChat(id: string, name: string): Promise<void> {
  const headers = jsonHeaders();
  if (!CHAT_API_URL || !headers) return;
  const res = await fetch(`${CHAT_API_URL}/chats/${id}/name`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(`Chats API ${res.status}`);
}

/** POST ${CHAT_API_URL}/chats/{id}/clone — returns `{ id }` of the new chat. */
export async function cloneChat(id: string): Promise<{ id: string } | null> {
  const headers = bearerHeaders();
  if (!CHAT_API_URL || !headers) return null;
  const res = await fetch(`${CHAT_API_URL}/chats/${id}/clone`, { method: 'POST', headers });
  if (!res.ok) throw new Error(`Chats API ${res.status}`);
  return (await res.json()) as { id: string };
}

/**
 * Navigate to a chat-web-app URL with a short-lived exchange token as a
 * `token=…` query param. The chat app accepts this in place of the
 * bounce-through-app handoff (chat → app for token → chat), which
 * eliminates the redirect roundtrip and the cross-account drift where
 * the app was logged into account A but chat was still on B.
 *
 * Navigation only happens once the token exchange succeeds — on failure
 * the user stays on the page and gets an error toast. The long-lived
 * session token is never used as a fallback.
 *
 * `_blank` opens a new tab; when the browser blocks the popup (the
 * user-gesture window can expire during the exchange await, notably on
 * iOS Safari) it falls back to same-tab navigation.
 *
 * Empty input is a no-op so the `if (!url) …` "is chat configured"
 * guards at call sites keep working.
 *
 * IMPORTANT: never put a tokened chat URL in an `<a href>` attribute.
 * Tokens in `href` are copyable via right-click "Copy link" and visible
 * in the DOM to inspection / extensions. Use `handleAuthedChatClick`
 * below for anchor elements — it keeps the href tokenless and only adds
 * the token at navigation time.
 *
 * `exit` is REQUIRED, not optional, on purpose: this function is the
 * chokepoint that 18 of the 20 agents-app exits funnel through, so making
 * the parameter mandatory turns the compiler into the checklist that every
 * one of them is labelled in Mixpanel. Without a label these cross-origin
 * navigations read as idle time with no destination.
 */
export async function openAuthedChatUrl(
  url: string,
  target: '_self' | '_blank' = '_self',
  exit: AgentsExitContext,
): Promise<void> {
  if (!url) return;

  // Tracked before the token exchange so a handoff the user initiated is
  // recorded even when the exchange fails and we never navigate. This is NOT
  // about giving the request time to flush: mixpanel-browser defaults to
  // `batch_requests: true`, so `track` only appends to a localStorage queue —
  // the delay buys nothing. Delivery on unload is mixpanel's own
  // visibilitychange/pagehide → sendBeacon handler, which covers the
  // synchronous `location.href` exits in User.ts too.
  trackingStore.agentsAppOpened({
    ...exit,
    url,
    requestedNewTab: target === '_blank',
  });

  try {
    const u = new URL(url);
    u.searchParams.set('token', await exchangeShortLivedToken());
    const href = u.toString();
    const opened = target === '_blank' ? window.open(href, '_blank') : null;
    // '_self', or popup blocked (gesture expired during the await) —
    // navigate the current tab.
    if (!opened) window.location.assign(href);
  } catch (error) {
    showToast('chat-open-error', { error });
  }
}

/**
 * Click handler for an `<a href={chatUrl}>` that navigates to the
 * token-decorated URL on plain left-click while leaving the visible
 * href tokenless. Modifier-clicks (cmd/ctrl/shift/alt) and non-primary
 * buttons fall through to the browser's default behavior so middle-
 * click and "open in new tab" still work — they'll land on chat
 * tokenless and chat will perform its existing redirect handoff.
 *
 * This is the path of least exposure: the token never enters the DOM,
 * is not copyable via "Copy link address," and is only set on the URL
 * at the moment of navigation (where it would end up anyway).
 *
 * Note the modifier/middle-click fall-through is deliberately NOT tracked:
 * those bypass this handler entirely and land on chat tokenless via the
 * browser's own navigation, so there's no hook to fire from.
 */
export function handleAuthedChatClick(
  href: string,
  exit: AgentsExitContext,
): (e: React.MouseEvent<HTMLElement>) => void {
  return (e) => {
    if (!href) return;
    if (e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    void openAuthedChatUrl(href, '_self', exit);
  };
}

/** Deep-link to a chat on the StarZero Agents web app. Tokenless — navigate
 *  via `openAuthedChatUrl` or pair the anchor with `handleAuthedChatClick`. */
export function getChatUrl(id: string): string {
  if (!CHAT_URL) return '';
  return `${CHAT_URL}/chat/${id}`;
}

/** Deep-link to the new-chat route on the StarZero Agents web app.
 *  Tokenless — see `getChatUrl`. Pass `libraryId` to scope the new chat
 *  to a library (same `?library=` convention as `askAgent`); the chat
 *  app reads it to preselect that library. */
export function getNewChatUrl(libraryId?: string): string {
  if (!CHAT_URL) return '';
  return libraryId ? `${CHAT_URL}/chat/new?library=${libraryId}` : `${CHAT_URL}/chat/new`;
}

/** Deep-link to the chats list route on the StarZero Agents web app.
 *  Tokenless — see `getChatUrl`. The chat app hosts the chat-search box; the
 *  nav item just lands the user there. */
export function getChatsUrl(): string {
  if (!CHAT_URL) return '';
  return `${CHAT_URL}/chats`;
}

/** Deep-link to the account-wide artifacts page on the StarZero Agents web
 *  app. Tokenless — see `getChatUrl`. */
export function getArtifactsUrl(): string {
  if (!CHAT_URL) return '';
  return `${CHAT_URL}/artifacts`;
}

/** Whether the chats API can be reached (HTTP). */
export function isChatsApiConfigured(): boolean {
  return !!CHAT_API_URL;
}

/** Whether the recent-chats UI should render at all. Needs both the API
 *  host (to fetch the list) AND the web app origin (to navigate when a
 *  chat is clicked). The "+ New Chat" button has a looser bar: it only
 *  needs `CHAT_URL`, since it never reads the API. */
export function canShowRecentChats(): boolean {
  return !!CHAT_API_URL && !!CHAT_URL;
}
